<template>
    <div class="notification-alert-stack" role="region" :aria-label="$t('Notifications')" aria-live="polite" aria-relevant="additions">
        <div
            v-for="alert in notificationAlerts"
            :key="alert.notification.id"
            class="notification-alert-entry"
            :class="{ 'notification-alert-leaving': alert.leaving }"
            :inert="alert.leaving"
            @mouseenter="setNotificationAlertInteraction(alert.notification.id, 'hovered', true)"
            @mouseleave="setNotificationAlertInteraction(alert.notification.id, 'hovered', false)"
            @focusin="setNotificationAlertInteraction(alert.notification.id, 'focused', true)"
            @focusout="onNotificationAlertFocusOut(alert.notification.id, $event)"
        >
            <NotificationAlertCard
                :notification="alert.notification"
                @open="openNotificationAlert(alert.notification)"
                @dismiss="dismissNotificationAlert(alert.notification.id)"
            />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { NotificationItem } from "@/api/definitions";
import { ApiNotifications } from "@/api/api-group-notifications";
import { NotificationsController } from "@/control/notifications";
import { AuthController } from "@/control/auth";
import { Request } from "@asanrom/request-browser";
import { getNotificationLink } from "@/utils/notification-content";
import NotificationAlertCard from "./NotificationAlertCard.vue";

interface NotificationAlertState {
    notification: NotificationItem;
    remaining: number;
    startedAt: number;
    timeout: ReturnType<typeof setTimeout> | null;
    hovered: boolean;
    focused: boolean;
    leaving: boolean;
}

export default defineComponent({
    components: { NotificationAlertCard },
    name: "NotificationAlertStack",
    data() {
        return {
            notificationAlerts: [] as NotificationAlertState[],
            pendingNotificationAlerts: [] as NotificationItem[],
        };
    },
    methods: {
        removeNotificationAlert: function (id: string) {
            const alert = this.notificationAlerts.find((item) => item.notification.id === id);
            if (alert?.timeout) clearTimeout(alert.timeout);
            this.notificationAlerts = this.notificationAlerts.filter((item) => item.notification.id !== id);
            this.fillNotificationAlerts();
        },
        clearNotificationAlerts: function () {
            for (const alert of this.notificationAlerts) {
                if (alert.timeout) clearTimeout(alert.timeout);
            }
            this.notificationAlerts = [];
            this.pendingNotificationAlerts = [];
        },
        showNotificationAlerts: function (notifications: NotificationItem[]) {
            if (
                NotificationsController.DoNotDisturb ||
                !AuthController.isAuthenticated() ||
                !AuthController.hasPermission("notification.read")
            )
                return;
            for (const notification of notifications.slice().sort((a, b) => a.timestamp - b.timestamp)) {
                if (
                    notification.read ||
                    this.notificationAlerts.some((alert) => alert.notification.id === notification.id) ||
                    this.pendingNotificationAlerts.some((item) => item.id === notification.id)
                )
                    continue;
                this.pendingNotificationAlerts.push(notification);
            }
            this.fillNotificationAlerts();
        },
        fillNotificationAlerts: function () {
            // A leaving card still occupies a slot until its exit animation has finished.
            while (this.notificationAlerts.length < 3 && this.pendingNotificationAlerts.length > 0) {
                const notification = this.pendingNotificationAlerts.shift();
                this.notificationAlerts.push({
                    notification,
                    remaining: 6000,
                    startedAt: 0,
                    timeout: null,
                    hovered: false,
                    focused: false,
                    leaving: false,
                });
                this.setNotificationAlertInteraction(notification.id, "hovered", false);
            }
        },
        setNotificationAlertInteraction: function (id: string, interaction: "hovered" | "focused", active: boolean) {
            const alert = this.notificationAlerts.find((item) => item.notification.id === id);
            if (!alert || alert.leaving) return;
            alert[interaction] = active;
            if (alert.timeout) {
                clearTimeout(alert.timeout);
                alert.timeout = null;
                alert.remaining = Math.max(0, alert.remaining - (Date.now() - alert.startedAt));
            }
            if (!alert.hovered && !alert.focused) {
                alert.startedAt = Date.now();
                alert.timeout = setTimeout(() => this.dismissNotificationAlert(id), alert.remaining);
            }
        },
        onNotificationAlertFocusOut: function (id: string, event: FocusEvent) {
            if ((event.currentTarget as HTMLElement).contains(event.relatedTarget as Node | null)) return;
            this.setNotificationAlertInteraction(id, "focused", false);
        },
        dismissNotificationAlert: function (id: string) {
            const alert = this.notificationAlerts.find((item) => item.notification.id === id);
            if (!alert || alert.leaving) return;
            if (alert.timeout) clearTimeout(alert.timeout);
            alert.leaving = true;
            alert.timeout = setTimeout(() => this.removeNotificationAlert(id), 180);
        },
        openNotificationAlert: function (notification: NotificationItem) {
            this.dismissNotificationAlert(notification.id);
            this.$router.push(getNotificationLink(notification) || { name: "notifications" });
            const session = AuthController.Session;
            Request.Do(ApiNotifications.MarkOneAsRead({ id: notification.id }))
                .onSuccess(() => {
                    if (session === AuthController.Session) NotificationsController.CheckNotifications();
                })
                .onRequestError((err, handleErr) => {
                    if (session !== AuthController.Session) return;
                    handleErr(err, {
                        unauthorized: () => this.$requireLogin(),
                        forbidden: () => this.$showErrorModal({ message: this.$t("Access denied") }),
                        badRequest: () => this.$showErrorModal({ message: this.$t("Bad request") }),
                        notFound: () => NotificationsController.CheckNotifications(),
                        serverError: () => this.$showErrorModal({ message: this.$t("Internal server error") }),
                        networkError: () => this.$showErrorModal({ message: this.$t("Could not connect to the server") }),
                    });
                })
                .onUnexpectedError((err) => console.error(err));
        },
    },
    mounted: function () {
        this.$listenOnAppEvent("notification-alerts", this.showNotificationAlerts.bind(this));
        this.$listenOnAppEvent("notification-alerts-clear", this.clearNotificationAlerts.bind(this));
    },
    beforeUnmount: function () {
        this.clearNotificationAlerts();
    },
});
</script>

<style scoped>
.notification-alert-stack {
    position: fixed;
    top: calc(var(--top-bar-size) + var(--space-4));
    right: var(--space-4);
    width: min(400px, calc(100% - 2 * var(--space-4)));
    max-height: calc(100dvh - var(--top-bar-size) - 2 * var(--space-4));
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    z-index: 40;
    pointer-events: none;
}
.notification-alert-entry {
    pointer-events: auto;
    animation: notification-alert-enter 180ms ease-out;
    transition:
        opacity 180ms ease-out,
        transform 180ms ease-out;
}
.notification-alert-leaving {
    animation: none;
    opacity: 0;
    transform: translateY(-8px);
    pointer-events: none;
}
@keyframes notification-alert-enter {
    from {
        opacity: 0;
        transform: translateY(-8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
@media (prefers-reduced-motion: reduce) {
    .notification-alert-entry {
        animation: none;
        transition: none;
    }
}
</style>
